import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Table, Tabs, Tag, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useRecoilState, useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import { dataKitsPageState, dataKitsState, memberState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto } from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { DataKitThumb } from "./DataKitThumb";
import { DataKitLocationAssignmentsTab } from "./DataKitLocationAssignmentsTab";
import { hasDataKitBeta } from "./dataKitAccess";
import "./DataKitListPage.scss";

const PAGE_SIZE = 100;

export function formatKitDate(value?: string): string {
  if (!value) return "––";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "––";
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) return "Today";
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function DataKitListPage() {
  const navigate = useNavigate();
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [kits, setKits] = useRecoilState(dataKitsState);
  const [page, setPage] = useRecoilState(dataKitsPageState);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    (includeArchived: boolean) => {
      setLoading(true);
      DataKitApi.listKits(includeArchived)
        .then((res) => setKits(res.data || []))
        .catch(() => message.error("Could not load data kits"))
        .finally(() => setLoading(false));
    },
    [setKits]
  );

  useEffect(() => {
    load(showArchived);
  }, [load, showArchived]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return kits;
    return kits.filter(
      (k) =>
        (k.name || "").toLowerCase().includes(q) ||
        (k.description || "").toLowerCase().includes(q)
    );
  }, [kits, query]);

  useEffect(() => {
    setPage(1);
  }, [query, showArchived, setPage]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const createKit = () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    DataKitApi.createKit({
      name,
      description: newDescription.trim(),
      archived: false,
      itemCount: 0,
      locationCount: 0,
    })
      .then((res) => {
        const created = res.data;
        setCreateOpen(false);
        setNewName("");
        setNewDescription("");
        if (created.id) navigate(`${env.dashboardPath}/account/data-kits/${created.id}`);
      })
      .catch(() => message.error("Could not create the data kit"))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitDto, b: DataKitDto) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (_: string, record: DataKitDto) => (
        <div className="data-kit-name-cell">
          <DataKitThumb name={record.name || "Untitled"} />
          <div className="data-kit-name-text">
            <div className="data-kit-name">
              {record.name || "Untitled"}
              {record.archived && <Tag className="data-kit-archived-tag">Archived</Tag>}
            </div>
            {record.description && (
              <div className="data-kit-description">{record.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Locations",
      dataIndex: "locationCount",
      key: "locationCount",
      width: 120,
      align: "center" as const,
      render: (count: number) => count ?? 0,
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 120,
      align: "center" as const,
      render: (count: number) => count ?? 0,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedDate",
      key: "updatedDate",
      width: 160,
      render: (_: string, record: DataKitDto) => formatKitDate(record.updatedDate),
    },
  ];

  if (!hasDataKitBeta(member)) return null;

  return (
    <div className="data-kit-list-page">
      <header className="data-kit-list-header">
        <div className="data-kit-list-header-text">
          <h1>Data Kits</h1>
          <p className="data-kit-subtitle">
            Account-level menu data. Assign a kit to any number of locations; a location can carry
            more than one.
          </p>
        </div>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="data-kit-new-btn"
            onClick={() => setCreateOpen(true)}
          >
            Data Kit
          </Button>
        )}
      </header>

      <Tabs
        className="data-kit-list-tabs"
        items={[
          {
            key: "kits",
            label: "Data Kits",
            children: (
              <>
                <div className="data-kit-toolbar">
                  <Input
                    className="data-kit-search"
                    size="large"
                    placeholder="Search data kits..."
                    prefix={<SearchOutlined style={{ color: "#999" }} />}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    allowClear
                  />
                  <button
                    type="button"
                    className={`data-kit-archived-toggle${showArchived ? " selected" : ""}`}
                    onClick={() => setShowArchived((v) => !v)}
                  >
                    {showArchived ? "Hide archived" : "Show archived"}
                  </button>
                </div>

                <Table
                  className="data-kit-table"
                  dataSource={pageItems}
                  columns={columns}
                  rowKey="id"
                  loading={loading}
                  rowClassName="data-kit-row"
                  pagination={
                    filtered.length > PAGE_SIZE
                      ? {
                          current: page,
                          pageSize: PAGE_SIZE,
                          total: filtered.length,
                          onChange: setPage,
                          showSizeChanger: false,
                        }
                      : false
                  }
                  locale={{
                    emptyText: query.trim()
                      ? "No data kits match your search"
                      : "No data kits yet",
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (!record.id) return;
                      navigate(`${env.dashboardPath}/account/data-kits/${record.id}`);
                    },
                  })}
                />
              </>
            ),
          },
          {
            key: "locations",
            label: "Location Assignments",
            children: <DataKitLocationAssignmentsTab kits={kits} onChanged={() => load(showArchived)} />,
          },
        ]}
      />

      <Modal
        className={"data-kit-create-modal clean one"}
        open={createOpen}
        width={600}
        title="New Data Kit"
        okText="Create"
        confirmLoading={saving}
        okButtonProps={{ disabled: !newName.trim() }}
        onOk={createKit}
        onCancel={() => setCreateOpen(false)}
      >
        <label className="data-kit-field">
          <span>Name</span>
          <Input
            size="large"
            placeholder="e.g. Truluck's Core"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={createKit}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="data-kit-field">
          <span>Description</span>
          <Input
            size="large"
            placeholder="e.g. Food, dinner & bar"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            maxLength={255}
          />
        </label>
      </Modal>
    </div>
  );
}
